import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { HeaderNav } from "@/components/HeaderNav";
import { DIM_COLOR } from "@/components/DimensionBar";
import api from "@/lib/api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { AccountDeletionRequest } from "@/components/AccountDeletionRequest";
import { GlimpseJourneyCard } from "@/components/glimpse/GlimpseJourneyCard";
import { contextLabel } from "@/components/ReflectionContextStep";

export default function IndividualDashboard() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const [portraits, setPortraits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chooser, setChooser] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cmpA, setCmpA] = useState("");
  const [cmpB, setCmpB] = useState("");
  const [cmpNote, setCmpNote] = useState(null);
  const [cmpNoteBusy, setCmpNoteBusy] = useState(false);
  const [access, setAccess] = useState(null);

  useEffect(() => { api.get("/me/assessment-access").then((r) => setAccess(r.data)).catch(() => {}); }, []);
  const [journey, setJourney] = useState(null);
  useEffect(() => { api.get("/glimpse/state").then((r) => setJourney(r.data.journey)).catch(() => {}); }, []);
  const needsPayment = access && !access.has_access;

  useEffect(() => { setCmpNote(null); }, [cmpA, cmpB]);

  useEffect(() => {
    api.get("/me/portraits").then((r) => setPortraits(r.data.portraits)).finally(() => setLoading(false));
  }, []);

  // Follow-up email "Reassess Your Authority" deep-link: once a newer independent portrait
  // exists, land pre-filled on the compare view (anchor vs latest); otherwise invite reassessment.
  useEffect(() => {
    if (loading || params.get("compare") !== "followup") return;
    const indep = portraits.filter((p) => p.dimension_scores && !p.organization_name && !p.is_demo && !p.is_test);
    if (indep.length >= 2) {
      const sorted = [...indep].sort((a, b) => (a.completed_at || "").localeCompare(b.completed_at || ""));
      setCmpA(sorted[0].session_id);
      setCmpB(sorted[sorted.length - 1].session_id);
      setTimeout(() => document.querySelector('[data-testid="portrait-compare"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 400);
    } else {
      setChooser(true);
    }
  }, [loading, portraits, params]);

  const takeAnother = () => { if (needsPayment && !portraits.length) { navigate("/payment"); return; } setChooser(true); };
  const chooseOrg = () => navigate("/onboarding"); // profile exists → starts at group/code step
  const chooseIndependent = async () => {
    setBusy(true);
    try { await api.post("/me/go-independent"); navigate(needsPayment ? "/payment" : "/consent"); }
    catch { navigate(needsPayment ? "/payment" : "/consent"); }
    finally { setBusy(false); }
  };

  // longitudinal series (aligned means over time)
  const dimKeys = portraits.length ? Object.keys(portraits[portraits.length - 1].dimension_scores || {}) : [];
  const series = portraits.map((p) => {
    const row = { date: (p.completed_at || "").slice(0, 10) };
    Object.entries(p.dimension_scores || {}).forEach(([k, v]) => { row[k] = v.aligned_mean; });
    return row;
  });
  const nameByKey = {};
  if (portraits.length) Object.entries(portraits[portraits.length - 1].dimension_scores || {}).forEach(([k, v]) => { nameByKey[k] = v.name; });

  return (
    <div>
      <HeaderNav />
      <div className="max-w-5xl mx-auto px-5 sm:px-8 pt-12 pb-24">
        <div className="flex flex-wrap items-end justify-between gap-4 cg-fade-up">
          <div>
            <p className="cg-eyebrow mb-3">Your workspace</p>
            <h1 className="font-serif text-4xl sm:text-5xl text-[#F3F4F6]">Welcome back, {user?.first_name || user?.name?.split(" ")[0]}.</h1>
            <p className="mt-2 text-[#9CA3AF]">Your portraits are private to you.</p>
          </div>
          {!(journey && journey.stage !== "complete") && <button data-testid="start-assessment-button" onClick={takeAnother}
            className="inline-flex items-center gap-2 bg-brass text-obsidian font-medium px-6 py-3 rounded-full cg-scale-btn hover:bg-amber2">
            {portraits.length ? "Take another assessment" : "Take the assessment"} <ArrowRight className="w-4 h-4" />
          </button>}
        </div>

        {journey && journey.stage !== "complete" && <div className="mt-8"><GlimpseJourneyCard journey={journey} variant="dashboard" /></div>}

        {needsPayment && !(journey && journey.stage === "invite") && (
          <div className="cg-card p-7 mt-8 border-brass/40" data-testid="unlock-card">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="cg-eyebrow mb-2">Unlock your assessment</p>
                <h2 className="font-serif text-2xl text-[#F3F4F6]">Your Authority Portrait awaits</h2>
                <p className="text-sm text-[#9CA3AF] mt-1 max-w-md">One payment of $59.95 CAD unlocks the full Caesar's Ghost assessment and your personal portrait.</p>
              </div>
              <button data-testid="unlock-pay-button" onClick={() => navigate("/payment")}
                className="inline-flex items-center gap-2 bg-brass text-obsidian font-medium px-6 py-3 rounded-full cg-scale-btn hover:bg-amber2 shrink-0">
                Unlock for $59.95 CAD <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {chooser && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-5" data-testid="reassess-chooser" onClick={() => setChooser(false)}>
            <div className="cg-card p-8 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
              <h2 className="font-serif text-2xl text-[#F3F4F6] mb-1">How are you taking Caesar's Ghost this time?</h2>
              <p className="text-sm text-[#9CA3AF] mb-6">Each assessment records its own organization, campaign and period — your account and portrait history stay with you.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <button data-testid="reassess-org" onClick={chooseOrg} disabled={busy}
                  className="border border-[var(--cg-border)] rounded-xl p-5 text-left hover:border-brass cg-scale-btn">
                  <p className="font-serif text-lg text-[#F3F4F6] mb-1">As part of an organization or group</p>
                  <p className="text-sm text-[#9CA3AF]">Enter a campaign code to join.</p>
                </button>
                <button data-testid="reassess-independent" onClick={chooseIndependent} disabled={busy}
                  className="border border-[var(--cg-border)] rounded-xl p-5 text-left hover:border-brass cg-scale-btn">
                  <p className="font-serif text-lg text-[#F3F4F6] mb-1">Independently</p>
                  <p className="text-sm text-[#9CA3AF]">No code — your results stay private to you.</p>
                </button>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <p className="mt-16 cg-eyebrow animate-pulse">Loading…</p>
        ) : portraits.length === 0 ? (
          <div className="cg-card p-10 mt-10 text-center" data-testid="no-portraits">
            <p className="font-serif text-2xl text-[#F3F4F6] mb-2">You haven't completed an assessment yet.</p>
            <p className="text-sm text-[#9CA3AF] mb-6">Begin to receive your first Authority Portrait.</p>
            {!(journey && journey.stage !== "complete") && <button onClick={takeAnother} className="bg-brass text-obsidian font-medium px-6 py-3 rounded-full cg-scale-btn hover:bg-amber2">Begin now</button>}
          </div>
        ) : (
          <>
            <div className="mt-10 grid gap-4" data-testid="portrait-list">
              {portraits.slice().reverse().map((p) => (
                <Link key={p.session_id} to={`/portrait/${p.session_id}`} data-testid={`portrait-item-${p.session_id}`}
                  className="cg-card p-6 flex items-center justify-between cg-scale-btn hover:border-brass/50 group">
                  <div>
                    <p className="text-[#F3F4F6] font-medium">Authority Portrait</p>
                    <p className="text-sm text-[#9CA3AF] font-mono">{(p.completed_at || "").slice(0, 10)}
                      {p.organization_name ? <span> · {p.organization_name} · {p.campaign_name}{p.period_label ? ` · ${p.period_label}` : ""}</span> : <span> · Independent</span>}
                      {p.reflection_context && <span data-testid={`portrait-context-${p.session_id}`}> · Reflecting on: {contextLabel(p.reflection_context)}</span>}
                      {p.archetype && <span className="text-brass"> · {p.archetype.name}</span>}
                      {p.is_demo && <span className="text-[#6B7280]"> · demo</span>}
                      {p.is_test && <span className="text-[#6B7280]"> · test</span>}</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-[#6B7280] group-hover:text-brass group-hover:translate-x-1 transition-all" />
                </Link>
              ))}
            </div>

            {portraits.length >= 2 && (
              <div className="cg-card p-7 mt-8" data-testid="longitudinal-chart">
                <h2 className="font-serif text-2xl text-[#F3F4F6] mb-1">Your patterns over time</h2>
                <p className="text-sm text-[#6B7280] mb-6">Compared only against your own earlier responses — never against colleagues.</p>
                <ResponsiveContainer width="100%" height={320}>
                  <LineChart data={series} margin={{ left: -20, right: 10 }}>
                    <XAxis dataKey="date" stroke="#6B7280" fontSize={11} />
                    <YAxis domain={[1, 5]} stroke="#6B7280" fontSize={11} />
                    <Tooltip contentStyle={{ background: "#1A1D27", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, color: "#F3F4F6" }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {dimKeys.map((k) => (
                      <Line key={k} type="monotone" dataKey={k} name={nameByKey[k]} stroke={DIM_COLOR[k]} strokeWidth={2} dot={{ r: 3 }} />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {portraits.filter((p) => p.dimension_scores).length >= 2 && (() => {
              const withScores = portraits.filter((p) => p.dimension_scores);
              const A = withScores.find((p) => p.session_id === cmpA);
              const B = withScores.find((p) => p.session_id === cmpB);
              const label = (p) => `${(p.completed_at || "").slice(0, 10)} · ${p.campaign_name || "Independent"}`;
              return (
                <div className="cg-card p-7 mt-8" data-testid="portrait-compare">
                  <h2 className="font-serif text-2xl text-[#F3F4F6] mb-1">Compare two of your portraits</h2>
                  <p className="text-sm text-[#6B7280] mb-5">Pick any two of your own assessments — across campaigns or reassessments. Compared only against yourself.</p>
                  <div className="grid sm:grid-cols-2 gap-3 mb-6">
                    <select data-testid="compare-a" className="bg-[#0D0E12] border border-[var(--cg-border)] rounded-lg px-3 py-2.5 text-sm text-[#F3F4F6] focus:border-brass outline-none" value={cmpA} onChange={(e) => setCmpA(e.target.value)}>
                      <option value="">Select portrait A…</option>{withScores.map((p) => <option key={p.session_id} value={p.session_id}>{label(p)}</option>)}
                    </select>
                    <select data-testid="compare-b" className="bg-[#0D0E12] border border-[var(--cg-border)] rounded-lg px-3 py-2.5 text-sm text-[#F3F4F6] focus:border-brass outline-none" value={cmpB} onChange={(e) => setCmpB(e.target.value)}>
                      <option value="">Select portrait B…</option>{withScores.map((p) => <option key={p.session_id} value={p.session_id}>{label(p)}</option>)}
                    </select>
                  </div>
                  {A && B && (
                    <div className="space-y-2" data-testid="compare-result">
                      <div className="grid grid-cols-[1fr_auto_auto_auto] gap-3 text-xs text-[#6B7280] font-mono px-1">
                        <span>Dimension</span><span className="text-right w-14">A</span><span className="text-right w-14">B</span><span className="text-right w-16">Δ (B−A)</span>
                      </div>
                      {dimKeys.map((k) => {
                        const a = A.dimension_scores?.[k]?.raw_mean; const b = B.dimension_scores?.[k]?.raw_mean;
                        const d = (a != null && b != null) ? +(b - a).toFixed(2) : null;
                        return (
                          <div key={k} className="grid grid-cols-[1fr_auto_auto_auto] gap-3 items-center border-b border-[var(--cg-border)] py-1.5">
                            <span className="text-sm text-[#B9BDC7]" style={{ borderLeft: `3px solid ${DIM_COLOR[k]}`, paddingLeft: 8 }}>{nameByKey[k] || k}</span>
                            <span className="text-right w-14 font-mono text-sm text-[#9CA3AF]">{a ?? "—"}</span>
                            <span className="text-right w-14 font-mono text-sm text-[#9CA3AF]">{b ?? "—"}</span>
                            <span className={`text-right w-16 font-mono text-sm ${d > 0 ? "text-[#7D9D86]" : d < 0 ? "text-terracotta" : "text-[#6B7280]"}`}>{d == null ? "—" : (d > 0 ? `+${d}` : d)}</span>
                          </div>
                        );
                      })}
                      <p className="text-xs text-[#6B7280] mt-3">Values are within-profile means on the 1–5 scale, not population norms.</p>
                      <div className="mt-4" data-testid="portrait-compare-note-wrap">
                        <button data-testid="portrait-compare-note-btn" onClick={async () => {
                          setCmpNoteBusy(true);
                          try { const r = await api.post("/me/portrait-compare/narrative", { session_a: cmpA, session_b: cmpB }); setCmpNote(r.data.note); }
                          catch (e) { toast.error(e.response?.data?.detail || "AI note is temporarily unavailable."); }
                          finally { setCmpNoteBusy(false); }
                        }} disabled={cmpNoteBusy}
                          className="inline-flex items-center gap-2 border border-[var(--cg-border)] text-sm text-[#9CA3AF] px-4 py-2 rounded-full cg-scale-btn hover:border-brass disabled:opacity-40">
                          <Sparkles className="w-4 h-4" /> {cmpNoteBusy ? "Reflecting…" : cmpNote ? "Regenerate note" : "Generate a reflective note on the shift"}
                        </button>
                        {cmpNote && (
                          <div className="mt-3 bg-[#0D0E12] border border-[var(--cg-border)] rounded-xl p-4" data-testid="portrait-compare-note">
                            <p className="cg-eyebrow mb-1">Reflective note · your own two portraits · cautious</p>
                            <p className="text-[#D6D9DF] leading-relaxed text-[0.95rem] whitespace-pre-line">{cmpNote}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </>
        )}
        <AccountDeletionRequest />
      </div>
    </div>
  );
}
